import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';
import { UploadIcon, FileIcon, DeleteIcon } from 'lucide-react';

export interface FileUploadCardProps {
  title: string;
  required: boolean;
  file: File | null;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  description: string;
  /** 赤字で表示される注意書き */
  warningText?: string;
  previousFileName?: string;
  usePrevious?: boolean;
  onTogglePrevious?: () => void;
}

/**
 * ファイルアップロードカードコンポーネント
 * CSVファイルのアップロード、前回ファイルの再利用をサポート
 */
export function FileUploadCard({
  title,
  required,
  file,
  onFileChange,
  onRemoveFile,
  description,
  warningText,
  previousFileName,
  usePrevious,
  onTogglePrevious,
}: FileUploadCardProps) {
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1">{title}</Typography>
            {required ? (
              <Chip label="必須" size="small" variant="outlined" color="error" />
            ) : (
              <Chip label="任意" size="small" variant="outlined" color="default" />
            )}
          </Box>
          {(file || usePrevious) && (
            <Button
              size="small"
              color="error"
              onClick={() => {
                if (file) {
                  onRemoveFile();
                } else if (usePrevious) {
                  onTogglePrevious?.();
                }
              }}
              startIcon={<DeleteIcon size={16} />}
            >
              削除
            </Button>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: warningText ? 1 : 2 }}>
          {description}
        </Typography>
        {warningText && (
          <Typography variant="body2" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
            ※ {warningText}
          </Typography>
        )}

        {file ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <FileIcon size={16} style={{ marginRight: 8 }} />
            <Typography variant="body2">{file.name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({(file.size / 1024).toFixed(1)} KB)
            </Typography>
          </Box>
        ) : usePrevious && previousFileName ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: 'success.light',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'success.main',
            }}
          >
            <FileIcon size={16} style={{ marginRight: 8 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="success.dark" sx={{ fontWeight: 'medium' }}>
                {previousFileName}
              </Typography>
              <Typography variant="caption" color="success.dark">
                前回実行時のファイル
              </Typography>
            </Box>
          </Box>
        ) : (
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon size={16} />}
            fullWidth
          >
            CSVファイルを選択
            <input type="file" accept=".csv" hidden onChange={onFileChange} />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
